import { useState, useRef } from "react";

import styles from "./summary.module.css";
import usePhase from "utils/usePhase";
import useStore from "utils/useStore";
import validate from "utils/validate";
import constants from "constants/fi.json";

import email from "images/email.svg";
import name from "images/name.svg";

interface InputProps {
  value: string | null;
  setValue: (value: string) => void;
  placeholder?: string;
  validate?: (value: string) => boolean;
  variant?: "name" | "email";
}

const Input = ({ value, setValue, placeholder, validate, variant }: InputProps) => {
  const [displayValue, setDisplayValue] = useState(value || "");
  const [isValid, setIsValid] = useState<boolean>(
    validate ? validate(value || "") : true
  );
  const focusSnapshot = useRef("");

  const classes = [styles.editingValue];

  if (!isValid) {
    classes.push(styles.error);
  }

  return (
    <div className={variant === undefined ? styles.flex : styles.flexWithIcon}>
      <input
        className={classes.join(" ")}
        placeholder={placeholder}
        value={displayValue}
        onFocus={() => {
          focusSnapshot.current = displayValue;
          setDisplayValue("");
        }}
        onChange={(e) => {
          const newValue = e.target.value;
          setDisplayValue(newValue);
          if (validate && !validate(newValue)) {
            setIsValid(false);
          } else {
            setIsValid(true);
            setValue(newValue);
          }
        }}
        onBlur={() => {
          if (displayValue === "") {
            setDisplayValue(focusSnapshot.current);
            setIsValid(validate ? validate(focusSnapshot.current) : true);
          } else if (!isValid) {
            setDisplayValue(focusSnapshot.current);
            setValue(focusSnapshot.current);
            setIsValid(validate ? validate(focusSnapshot.current) : true);
          }
        }}
      />
      {variant === "email" && (
        <div>
          <img src={email} alt="" />
        </div>
      )}
      {variant === "name" && (
        <div>
          <img src={name} alt="" />
        </div>
      )}
    </div>
  );
};

const Contact = () => {
  const t = usePhase("summary");
  const ordererName = useStore((state) => state.ordererName);
  const ordererPhone = useStore((state) => state.ordererPhone);
  const ordererEmail = useStore((state) => state.ordererEmail);
  const ordererRelationship = useStore((state) => state.ordererRelationship);
  const cost = useStore((state) => state.calculateCost());

  const setUserSelection = useStore((state) => state.setUserSelection);

  const setOrdererName = (value: string) =>
    setUserSelection("ordererName", value);
  const setOrdererEmail = (value: string) =>
    setUserSelection("ordererEmail", value);
  const setOrderedPhone = (value: string) =>
    setUserSelection("ordererPhone", value);
  const setOrdererRelationship = (value: string) =>
    setUserSelection("ordererRelationship", value);

  return (
    <>
      <div className={styles.block}>
        <div className={styles.total}>
          <div className={[styles.toprow, "h3"].join(" ")}>
            <h3 className={styles.totaltitle}>{constants.estimated_cost} {constants.in_total.toLowerCase()}</h3>
            <h3 className={styles.totalamount}>{cost} €</h3>
          </div>
          <div className={styles.total}>{t("disclaimer")}</div>
        </div>
      </div>
      <div className={[styles.block, styles.notopmargin].join(" ")}>
        <div className={styles.subtitle}>
          <h3 className="h3">{t("contact")}</h3>
        </div>
        <div className={styles.content}>
          <div className={styles.row}>
            <Input
              setValue={setOrdererName}
              value={ordererName}
              placeholder={constants.placeholder_orderer_name}
              validate={(value) => value !== ""}
              variant="name"
            />
          </div>
          <div className={styles.row}>
            <Input
              setValue={setOrdererEmail}
              value={ordererEmail}
              placeholder={constants.placeholder_orderer_email}
              validate={validate.email}
              variant="email"
            />
          </div>
          <div className={styles.row}>
            <Input
              setValue={setOrderedPhone}
              value={ordererPhone}
              placeholder={constants.placeholder_orderer_phone}
            />
          </div>
          <div className={styles.row}>
            <Input
              setValue={setOrdererRelationship}
              value={ordererRelationship}
              placeholder={constants.placeholder_orderer_relationship}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
